'use client';
import { cn } from '@/utils/cn';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

export type BorderTrailProps = {
  className?: string;
  size?: number;
  duration?: number;
  ease?: string;
  onAnimationComplete?: () => void;
  style?: React.CSSProperties;
};

export function BorderTrail({
  className,
  size = 60,
  duration = 5,
  ease = 'none',
  onAnimationComplete,
  style,
}: BorderTrailProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<gsap.core.Tween | null>(null);

  useEffect(() => {
    if (!elementRef.current) return;

    // initial position
    gsap.set(elementRef.current, {
      offsetDistance: '0%'
    });

    const animation = gsap.to(elementRef.current, {
      offsetDistance: '100%',
      duration,
      ease,
      repeat: -1,
      onComplete: onAnimationComplete,
    });

    animationRef.current = animation;

    return () => {
      animation.kill();
    };
  }, [size, duration, ease, onAnimationComplete]);

  return (
    <div className={cn(
      'pointer-events-none absolute inset--.25 p-.25 rounded-[inherit] overflow-hidden',
      '[mask-image:linear-gradient(#fff_0_0),linear-gradient(#fff_0_0)] [mask-clip:content-box,border-box] [mask-origin:content-box,border-box] [mask-composite:exclude] [-webkit-mask-composite:xor]'
    )}>
      <div
        ref={elementRef}
        className={cn('absolute aspect-square bg-red blur-[8px]', className)}
        style={{
          width: size,
          offsetPath: `rect(0 auto auto 0 round ${size}px)`,
          offsetDistance: '0%',
          ...style,
        }}
      />
    </div>
  );
}
